import React, { useState, useEffect } from 'react';
import {
  Home,
  FileText,
  Award,
  MessageSquare,
  Calendar,
  School,
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Target,
  Eye,
  Download,
  Filter,
  ChevronRight,
  User,
  BarChart3,
  BookOpen,
  Send,
  ThumbsUp,
  MessageCircle,
  Star
} from 'lucide-react';
import { useSharingContext } from '../../contexts/SharingContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { format, differenceInDays } from 'date-fns';

const ParentPortal = () => {
  const { parentSharedItems, loadSharingData, addComment } = useSharingContext();
  const { currentUser } = useFamilyContext();
  const [activeTab, setActiveTab] = useState('overview');
  const [sharedItems, setSharedItems] = useState([]);
  const [parentData, setParentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [commentText, setCommentText] = useState('');

  useEffect(() => {
    if (currentUser) {
      loadParentData();
      // Set shared items from context
      setSharedItems(parentSharedItems || []);
    }
  }, [currentUser, parentSharedItems]);

  const loadParentData = async () => {
    try {
      setLoading(true);
      await loadSharingData();

      // For now, use mock data for timeline and college data
      // This can be replaced with real data later
      setParentData({
        timeline: {
          completionRate: 75,
          recentCompletions: [
            { task: 'Common App Essay Draft', completedAt: new Date().toISOString() },
            { task: 'SAT Registration', completedAt: new Date(Date.now() - 86400000).toISOString() }
          ],
          upcomingDeadlines: [
            { task: 'UC Application', date: new Date(Date.now() + 604800000).toISOString(), daysLeft: 7 },
            { task: 'Stanford Essay', date: new Date(Date.now() + 1209600000).toISOString(), daysLeft: 14 }
          ]
        },
        colleges: [
          {
            id: 1,
            name: 'Stanford University',
            category: 'reach',
            status: 'in_progress',
            deadline: new Date(Date.now() + 2592000000).toISOString(),
            essaysComplete: 2,
            essaysTotal: 5,
            completionPercent: 40,
            visited: true
          },
          {
            id: 2,
            name: 'UC Berkeley',
            category: 'target',
            status: 'not_started',
            deadline: new Date(Date.now() + 3456000000).toISOString(),
            essaysComplete: 0,
            essaysTotal: 4,
            completionPercent: 0,
            visited: false
          }
        ]
      });
    } catch (error) {
      console.error('Error loading parent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (itemId) => {
    if (!commentText.trim()) return;

    try {
      await addComment(itemId, commentText);
      setCommentText('');
      await loadSharingData();
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-azure-600 to-azure-700 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">Welcome back, Sarah!</h2>
        <p className="opacity-90">
          Your student is making great progress on their college applications.
        </p>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-3xl font-bold">{parentData?.timeline?.completionRate}%</p>
            <p className="text-sm opacity-90">Tasks Complete</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-3xl font-bold">{parentData?.colleges?.length}</p>
            <p className="text-sm opacity-90">Colleges Listed</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-3xl font-bold">{sharedItems.length}</p>
            <p className="text-sm opacity-90">Items Shared</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Clock className="text-azure-600" size={20} />
          Recent Activity
        </h3>
        <div className="space-y-3">
          {parentData?.timeline?.recentCompletions?.map((task, idx) => (
            <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-emerald-500" size={18} />
                <div>
                  <p className="font-medium">{task.task}</p>
                  <p className="text-sm text-stone-500">
                    Completed {format(new Date(task.completedAt), 'MMM d')}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming Deadlines */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <AlertCircle className="text-orange-600" size={20} />
          Upcoming Deadlines
        </h3>
        <div className="space-y-3">
          {parentData?.timeline?.upcomingDeadlines?.map((deadline, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
              <div>
                <p className="font-medium">{deadline.task}</p>
                <p className="text-sm text-stone-600">
                  Due {format(new Date(deadline.date), 'MMM d, yyyy')}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                deadline.daysLeft <= 7
                  ? 'bg-red-100 text-red-700'
                  : deadline.daysLeft <= 14
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-emerald-100 text-emerald-700'
              }`}>
                {deadline.daysLeft} days
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const SharedItemsTab = () => (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button className="px-4 py-2 bg-azure-600 text-white rounded-lg">All Items</button>
          <button className="px-4 py-2 bg-white border rounded-lg hover:bg-stone-50">Essays</button>
          <button className="px-4 py-2 bg-white border rounded-lg hover:bg-stone-50">Brag Sheet</button>
          <button className="px-4 py-2 bg-white border rounded-lg hover:bg-stone-50">Recommendations</button>
        </div>
        <button className="px-4 py-2 bg-white border rounded-lg hover:bg-stone-50 flex items-center gap-2">
          <Filter size={18} />
          Filter
        </button>
      </div>

      {/* Shared Items List */}
      <div className="grid gap-4">
        {sharedItems.map(item => (
          <div key={item.id} className="bg-white rounded-xl border p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {item.type === 'essay' && <FileText className="text-azure-600" size={20} />}
                  {item.type === 'brag_sheet' && <Award className="text-violet-600" size={20} />}
                  {item.type === 'recommendation' && <MessageSquare className="text-emerald-600" size={20} />}
                  <h3 className="text-lg font-semibold">{item.title}</h3>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    item.status === 'final'
                      ? 'bg-emerald-100 text-emerald-700'
                      : item.status === 'complete'
                      ? 'bg-azure-100 text-azure-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {item.status}
                  </span>
                </div>
                <p className="text-sm text-stone-500">
                  Shared {format(new Date(item.sharedAt), 'MMM d, yyyy at h:mm a')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 bg-stone-100 rounded-lg hover:bg-stone-200 flex items-center gap-2">
                  <Eye size={16} />
                  View
                </button>
                <button className="px-3 py-1.5 bg-stone-100 rounded-lg hover:bg-stone-200 flex items-center gap-2">
                  <Download size={16} />
                  Download
                </button>
              </div>
            </div>

            {/* Item Content Preview */}
            {item.type === 'essay' && (
              <div className="bg-stone-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-stone-700 line-clamp-3">{item.content}</p>
                <p className="text-xs text-stone-500 mt-2">
                  {item.college} • {item.wordCount} words
                </p>
              </div>
            )}

            {item.type === 'brag_sheet' && (
              <div className="bg-stone-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium mb-2">Key Activities</p>
                    <ul className="text-sm text-stone-600 space-y-1">
                      {item.content.activities.slice(0, 2).map((activity, idx) => (
                        <li key={idx}>• {activity}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-2">Achievements</p>
                    <ul className="text-sm text-stone-600 space-y-1">
                      {item.content.achievements.slice(0, 2).map((achievement, idx) => (
                        <li key={idx}>• {achievement}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Comments Section */}
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle size={18} className="text-stone-500" />
                <h4 className="font-medium">Parent Feedback</h4>
              </div>

              {item.parentComments && item.parentComments.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {item.parentComments.map(comment => (
                    <div key={comment.id} className="bg-azure-50 rounded-lg p-3">
                      <p className="text-sm">{comment.text}</p>
                      <p className="text-xs text-stone-500 mt-1">
                        — {comment.author}, {format(new Date(comment.timestamp), 'MMM d')}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add encouraging feedback or suggestions..."
                  className="flex-1 px-3 py-2 border rounded-lg text-sm"
                />
                <button
                  onClick={() => handleAddComment(item.id)}
                  className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 flex items-center gap-2"
                >
                  <Send size={16} />
                  Send
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const TimelineTab = () => (
    <div className="space-y-6">
      {/* Progress Overview */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Application Timeline Progress</h3>
          <span className="text-2xl font-bold text-azure-600">
            {parentData?.timeline?.completionRate}%
          </span>
        </div>
        <div className="w-full bg-stone-200 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-azure-500 to-azure-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${parentData?.timeline?.completionRate}%` }}
          />
        </div>
      </div>

      {/* Timeline View */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-semibold mb-4">Upcoming Milestones</h3>
        <div className="relative">
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-stone-200"></div>
          {parentData?.timeline?.upcomingDeadlines?.map((deadline, idx) => (
            <div key={idx} className="relative flex items-start gap-4 mb-6">
              <div className={`w-4 h-4 rounded-full z-10 ${
                idx === 0 ? 'bg-orange-500' : 'bg-stone-300'
              }`}></div>
              <div className="flex-1">
                <div className="bg-stone-50 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{deadline.task}</h4>
                      <p className="text-sm text-stone-600 mt-1">
                        Due {format(new Date(deadline.date), 'MMMM d, yyyy')}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      deadline.daysLeft <= 7
                        ? 'bg-red-100 text-red-700'
                        : deadline.daysLeft <= 14
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {deadline.daysLeft} days left
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Completed Tasks */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-semibold mb-4">Recently Completed</h3>
        <div className="space-y-2">
          {parentData?.timeline?.recentCompletions?.map((task, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
              <CheckCircle2 className="text-emerald-600" size={20} />
              <div className="flex-1">
                <p className="font-medium">{task.task}</p>
                <p className="text-sm text-stone-600">
                  Completed {format(new Date(task.completedAt), 'MMM d, yyyy')}
                </p>
              </div>
              <ThumbsUp className="text-emerald-600" size={18} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const CollegesTab = () => (
    <div className="space-y-6">
      {/* College Categories Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl p-4 text-white">
          <h3 className="font-semibold mb-2">Reach Schools</h3>
          <p className="text-3xl font-bold">
            {parentData?.colleges?.filter(c => c.category === 'reach').length}
          </p>
        </div>
        <div className="bg-gradient-to-br from-azure-500 to-azure-600 rounded-xl p-4 text-white">
          <h3 className="font-semibold mb-2">Target Schools</h3>
          <p className="text-3xl font-bold">
            {parentData?.colleges?.filter(c => c.category === 'target').length}
          </p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
          <h3 className="font-semibold mb-2">Safety Schools</h3>
          <p className="text-3xl font-bold">
            {parentData?.colleges?.filter(c => c.category === 'safety').length}
          </p>
        </div>
      </div>

      {/* College List */}
      <div className="bg-white rounded-xl border">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">College Application List</h3>
        </div>
        <div className="divide-y">
          {parentData?.colleges?.map(college => (
            <div key={college.id} className="p-6 hover:bg-stone-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${
                    college.category === 'reach' ? 'bg-violet-500' :
                    college.category === 'target' ? 'bg-azure-500' :
                    'bg-emerald-500'
                  }`}></div>
                  <div>
                    <h4 className="font-semibold text-lg">{college.name}</h4>
                    <div className="flex items-center gap-4 text-sm text-stone-600 mt-1">
                      <span className="capitalize">{college.category} School</span>
                      <span>•</span>
                      <span>{college.status.replace('_', ' ').toUpperCase()}</span>
                      <span>•</span>
                      <span>Deadline: {format(new Date(college.deadline), 'MMM d')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-sm text-stone-500">Essays</p>
                    <p className="font-semibold">
                      {college.essaysComplete}/{college.essaysTotal}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-stone-500">Progress</p>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-stone-200 rounded-full h-2">
                        <div
                          className="bg-azure-600 h-2 rounded-full"
                          style={{ width: `${college.completionPercent}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium">{college.completionPercent}%</span>
                    </div>
                  </div>
                  {college.visited && (
                    <div className="flex items-center gap-1 text-emerald-600">
                      <CheckCircle2 size={18} />
                      <span className="text-sm">Visited</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-azure-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Home className="text-azure-600" size={24} />
              <h1 className="text-xl font-bold">Parent Portal</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-stone-600">
                Viewing: <strong>Alex's Application Journey</strong>
              </span>
              <button className="p-2 hover:bg-stone-100 rounded-lg">
                <User size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-6">
            {[
              { id: 'overview', label: 'Overview', icon: Home },
              { id: 'shared', label: 'Shared Items', icon: FileText },
              { id: 'timeline', label: 'Timeline', icon: Calendar },
              { id: 'colleges', label: 'Colleges', icon: School }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-azure-600 text-azure-600'
                    : 'border-transparent text-stone-600 hover:text-stone-900'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'shared' && <SharedItemsTab />}
        {activeTab === 'timeline' && <TimelineTab />}
        {activeTab === 'colleges' && <CollegesTab />}
      </div>
    </div>
  );
};

export default ParentPortal;